import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  isLoading: false,
  error: null,
  stockTransfers: [],
  stockTransfer: null,
};

const slice = createSlice({
  name: "stockTransfer",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    getStockTransfersSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.stockTransfers = action.payload;
    },

    getStockTransferSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.stockTransfer = action.payload;
    },

    updateStockTransferStatus(state, action) {
      const { id, status, cancellationReason } = action.payload;

      const transferIndex = state.stockTransfers.findIndex(
        (transfer) => transfer.id === id,
      );

      if (transferIndex !== -1) {
        state.stockTransfers[transferIndex].status = status;

        if (cancellationReason) {
          state.stockTransfers[transferIndex].cancellationReason =
            cancellationReason;
        }
      }

      if (state.stockTransfer?.id === id) {
        state.stockTransfer.status = status;

        if (cancellationReason) {
          state.stockTransfer.cancellationReason = cancellationReason;
        }
      }
    },

    resetStockTransferStore() {
      return initialState;
    },
  },
});

export default slice.reducer;

export const { updateStockTransferStatus } = slice.actions;

export function getStockTransfers(
  venueId,
  levelId,
  startDate = null,
  endDate = null,
) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const params = {
        venueId,
        levelId,
        // isDeleted: false,
        isActiveStockTransfer: false,
        limit: 100000000,
      };

      if (startDate) {
        params.startDate = startDate;
      }

      if (endDate) {
        params.endDate = endDate;
      }

      const response = await axios.get(`${backendUrl}/stockTransfer`, {
        params,
      });

      const results = response.data?.results ?? response.data ?? [];

      if (!Array.isArray(results)) {
        throw new Error("Invalid Stock Transfer list response.");
      }

      // Draft Stock Transfers belong to the Warehouse and must not appear
      // on the Venue portal.
      const filteredTransfers = results.filter(
        (transfer) => transfer.status !== "draft",
      );

      dispatch(slice.actions.getStockTransfersSuccess(filteredTransfers));

      return {
        data: filteredTransfers,
        status: response.status,
      };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function getStockTransfer(transferId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${backendUrl}/stockTransfer/${transferId}`,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function receiveStockTransfer(transferId, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.post(
        `${backendUrl}/stockTransfer/${transferId}/receive`,
        data,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      dispatch(
        slice.actions.updateStockTransferStatus({
          id: transferId,
          status: "received",
        }),
      );

      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function cancelStockTransfer(transferId, cancellationReason) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.post(
        `${backendUrl}/stockTransfer/${transferId}/cancel`,
        {
          cancellationReason,
        },
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      dispatch(
        slice.actions.updateStockTransferStatus({
          id: transferId,
          status: "cancelled",
          cancellationReason,
        }),
      );

      return {
        data: response.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error));
      throw error;
    }
  };
}

export function clearStockTransferAfterUpdating() {
  return async (dispatch) => {
    dispatch(slice.actions.resetStockTransferStore());
  };
}
